import { useState, useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { ScrollArea } from '@/components/ui/scroll-area'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { ManualOrderFilterBar } from './components/ManualOrderFilterBar'
import { ManualOrderTable } from './components/ManualOrderTable'
import { ManualOrderDetailDrawer } from './components/ManualOrderDetailDrawer'
import { ManualOrderPaymentDialog } from './components/ManualOrderPaymentDialog'
import { ManualOrderConfirmPaymentDialog } from './components/ManualOrderConfirmPaymentDialog'
import { ManualOrderCancelDialog } from './components/ManualOrderCancelDialog'
import { ManualOrderRefundDialog } from './components/ManualOrderRefundDialog'
import { ManualOrderDeliveryDialog } from './components/ManualOrderDeliveryDialog'
import { ManualOrderReturnDialog } from './components/ManualOrderReturnDialog'
import { ManualOrderDeleteDialog } from './components/ManualOrderDeleteDialog'
import { ExpressTrackingTimeline } from './components/ExpressTrackingTimeline'
import {
  useApplyEmployeeManualOrderRefund,
  useBatchDeleteEmployeeManualOrders,
  useCancelEmployeeManualOrder,
  useConfirmEmployeeManualOrderPayment,
  useDeleteEmployeeManualOrder,
  useDeliveryEmployeeManualOrder,
  useEmployeeManualOrder,
  useEmployeeManualOrders,
  useImportEmployeeManualOrderDeliveryExcel,
  useMarkManualOrderReturned,
  usePreviewEmployeeManualOrderShippingLists,
} from '@/hooks/useEmployeeManualOrder'
import { showError } from '@/lib/error'
import type {
  EmployeeManualOrder,
  EmployeeManualOrderExportFilterParams,
  EmployeeManualOrderListParams,
  ManualOrderRefundEligibility,
  EmployeeManualOrderExportRequest,
} from '@/api/employee-manual-order'
import { employeeManualOrderApi } from '@/api/employee-manual-order'
import { usePermissionStore } from '@/stores/permission'
import { useQueryClient } from '@tanstack/react-query'
import { employeeManualOrderKeys } from '@/hooks/useEmployeeManualOrder'

interface RefundDialogState {
  order: EmployeeManualOrder
  eligibility?: ManualOrderRefundEligibility
}

function buildManualOrderExportFilter(
  params: EmployeeManualOrderListParams
): EmployeeManualOrderExportFilterParams {
  const { status, ...rest } = params
  const numericStatus = typeof status === 'string' ? Number(status) : status

  return Number.isFinite(numericStatus) ? { ...rest, status: numericStatus } : rest
}

export function EmployeeManualOrdersPage() {
  const queryClient = useQueryClient()
  const [params, setParams] = useState<EmployeeManualOrderListParams>({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  })
  const [draftParams, setDraftParams] = useState<EmployeeManualOrderListParams>({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  })
  const [detailOrderId, setDetailOrderId] = useState<number | null>(null)
  const [trackingOrder, setTrackingOrder] = useState<EmployeeManualOrder | null>(null)
  const [paymentOrderId, setPaymentOrderId] = useState<number | null>(null)
  const [confirmPaymentOrder, setConfirmPaymentOrder] = useState<EmployeeManualOrder | null>(null)
  const [cancelOrder, setCancelOrder] = useState<EmployeeManualOrder | null>(null)
  const [deliveryOrder, setDeliveryOrder] = useState<EmployeeManualOrder | null>(null)
  const [refundDialog, setRefundDialog] = useState<RefundDialogState | null>(null)
  const [returnDialog, setReturnDialog] = useState<{ order: EmployeeManualOrder } | null>(null)
  const [pendingRefundOrderId, setPendingRefundOrderId] = useState<number | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [deleteOrder, setDeleteOrder] = useState<EmployeeManualOrder | null>(null)
  const [batchDeleteIds, setBatchDeleteIds] = useState<number[] | null>(null)
  const importInputRef = useRef<HTMLInputElement>(null)

  const { data, isLoading } = useEmployeeManualOrders(params)
  const confirmPaymentMutation = useConfirmEmployeeManualOrderPayment()
  const cancelMutation = useCancelEmployeeManualOrder()
  const deleteMutation = useDeleteEmployeeManualOrder()
  const batchDeleteMutation = useBatchDeleteEmployeeManualOrders()
  const applyRefundMutation = useApplyEmployeeManualOrderRefund()
  const markReturnedMutation = useMarkManualOrderReturned()
  const deliveryMutation = useDeliveryEmployeeManualOrder()
  const importDeliveryMutation = useImportEmployeeManualOrderDeliveryExcel()
  const previewShippingListsMutation = usePreviewEmployeeManualOrderShippingLists()
  const canConfirmPayment = usePermissionStore((state) => state.hasPermission('order:manual:confirm_payment'))
  const canApplyRefund = usePermissionStore((state) => state.hasPermission('order:manual:apply_refund'))
  const canCancel = usePermissionStore((state) => state.hasPermission('order:manual:cancel'))
  const canDelivery = usePermissionStore((state) => state.hasPermission('order:manual:delivery'))
  const canMarkRefundReturned = usePermissionStore((state) => state.hasPermission('order:manual:refund_return'))
  const canExport = usePermissionStore((state) => state.hasPermission('order:manual:export'))
  const canDelete = usePermissionStore((state) => state.hasPermission('order:manual:delete'))

  const { data: detail, isLoading: isDetailLoading } = useEmployeeManualOrder(
    detailOrderId ?? 0,
    detailOrderId !== null,
    params.scope
  )

  const handleDraftParamsChange = useCallback((newParams: Partial<EmployeeManualOrderListParams>) => {
    setDraftParams((prev) => ({
      ...prev,
      ...newParams,
      page: 1,
    }))
  }, [])

  const handleStatusChange = useCallback((status: EmployeeManualOrderListParams['status']) => {
    setDraftParams((prev) => {
      const nextParams = {
        ...prev,
        status,
        page: 1,
      }
      setParams(nextParams)
      return nextParams
    })
  }, [])

  const handleSearch = useCallback(() => {
    setParams({ ...draftParams, page: 1 })
  }, [draftParams])

  const handleReset = useCallback(() => {
    const nextParams = { page: 1, page_size: DEFAULT_PAGE_SIZE }
    setDraftParams(nextParams)
    setParams(nextParams)
  }, [])

  const exportManualOrders = useCallback(async (request: EmployeeManualOrderExportRequest) => {
    setIsExporting(true)
    try {
      await employeeManualOrderApi.exportExcel(request)
      toast.success('导出已开始')
    } catch (error) {
      showError(error, '导出失败')
    } finally {
      setIsExporting(false)
    }
  }, [])

  const handleFilterExport = useCallback(() => {
    exportManualOrders({
      mode: 'filter',
      filter: buildManualOrderExportFilter({ ...draftParams, page: 1 }),
    })
  }, [draftParams, exportManualOrders])

  const handleSelectedExport = useCallback((ids: number[]) => {
    if (ids.length === 0) {
      toast.error('请先勾选要导出的订单')
      return
    }
    exportManualOrders({
      mode: 'selected',
      ids,
    })
  }, [exportManualOrders])

  const handleViewDetail = useCallback((order: EmployeeManualOrder) => {
    setDetailOrderId(order.id)
  }, [])

  const handleManualPayment = useCallback((order: EmployeeManualOrder) => {
    setPaymentOrderId(order.id)
  }, [])

  const handleConfirmPayment = useCallback((order: EmployeeManualOrder) => {
    setConfirmPaymentOrder(order)
  }, [])

  const handleSubmitConfirmPayment = useCallback(() => {
    if (!confirmPaymentOrder) return

    confirmPaymentMutation.mutate(
      { id: confirmPaymentOrder.id },
      {
        onSuccess: () => {
          setConfirmPaymentOrder(null)
        },
        onError: (error) => showError(error, '确认收款失败'),
      }
    )
  }, [confirmPaymentMutation, confirmPaymentOrder])

  const handleCancel = useCallback((order: EmployeeManualOrder) => {
    setCancelOrder(order)
  }, [])

  const handleDelete = useCallback((order: EmployeeManualOrder) => {
    setDeleteOrder(order)
  }, [])

  const handleBatchDelete = useCallback((ids: number[]) => {
    setBatchDeleteIds(ids)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    if (!deleteOrder) return
    deleteMutation.mutate(
      { id: deleteOrder.id },
      {
        onSuccess: () => setDeleteOrder(null),
        onError: (error) => showError(error, '删除订单失败'),
      }
    )
  }, [deleteMutation, deleteOrder])

  const handleConfirmBatchDelete = useCallback(() => {
    if (!batchDeleteIds || batchDeleteIds.length === 0) return
    batchDeleteMutation.mutate(
      { ids: batchDeleteIds },
      {
        onSuccess: () => setBatchDeleteIds(null),
        onError: (error) => showError(error, '批量删除失败'),
      }
    )
  }, [batchDeleteMutation, batchDeleteIds])

  const handleDelivery = useCallback((order: EmployeeManualOrder) => {
    setDeliveryOrder(order)
  }, [])

  const handleBatchPrint = useCallback((ids: number[]) => {
    previewShippingListsMutation.mutate(
      { ids },
      {
        onSuccess: async (result) => {
          if (result.skipped.length > 0) {
            toast.warning(`已跳过 ${result.skipped.length} 个不可打印订单`)
          }

          if (result.orders.length === 0) {
            toast.error(result.skipped[0]?.reason || '没有可打印订单')
            return
          }

          try {
            // 用可打印订单的 ID 获取服务端渲染的 HTML
            const printableIds = result.orders.map((o) => o.id)
            const html = await employeeManualOrderApi.fetchShippingListHTML(printableIds)
            const printWindow = window.open('', '_blank')
            if (!printWindow) {
              toast.error('打印窗口打开失败，请检查浏览器弹窗设置')
              return
            }
            printWindow.document.write(html)
            printWindow.document.close()
            printWindow.focus()
          } catch (error) {
            showError(error, '获取打印页面失败')
          }
        },
        onError: (error) => showError(error, '预览发货清单失败'),
      }
    )
  }, [previewShippingListsMutation])

  const handleDeliveryImportChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const importParams = {
      scope: draftParams.scope,
      department_id: draftParams.department_id,
    }

    importDeliveryMutation.mutate(
      { file, params: importParams },
      {
        onSuccess: (result) => {
          const importResult = result as typeof result & {
            success_count?: number
            failed_count?: number
            failed_rows?: Array<{ row: number; order_id?: string; message: string }>
          }
          const successCount = importResult.success_count ?? result.success
          const failedCount = importResult.failed_count ?? result.failed
          const failedRows = importResult.failed_rows ?? result.rows.filter((row) => !row.success)

          toast.success(`导入完成：成功 ${successCount} 条，失败 ${failedCount} 条`)
          failedRows.slice(0, 5).forEach((row) => {
            toast.error(`第 ${row.row} 行 ${row.order_id || '-'}：${row.message}`)
          })
        },
        onError: (error) => showError(error, '导入失败'),
      }
    )
  }, [draftParams.department_id, draftParams.scope, importDeliveryMutation])

  const handleApplyRefund = useCallback((order: EmployeeManualOrder) => {
    setRefundDialog({
      order,
      eligibility: detail?.refund_eligibility,
    })
  }, [detail?.refund_eligibility])

  const handleApplyRefundFromList = useCallback((order: EmployeeManualOrder) => {
    setPendingRefundOrderId(order.id)
    setDetailOrderId(order.id)
  }, [])

  const handleDetailOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setDetailOrderId(null)
      setPendingRefundOrderId(null)
    }
  }, [])

  useEffect(() => {
    if (
      pendingRefundOrderId !== null &&
      detail?.order.id === pendingRefundOrderId &&
      detail.refund_eligibility?.can_apply &&
      refundDialog === null
    ) {
      setRefundDialog({
        order: detail.order,
        eligibility: detail.refund_eligibility,
      })
      setPendingRefundOrderId(null)
    }
  }, [detail, pendingRefundOrderId, refundDialog])

  const handleConfirmCancel = useCallback(() => {
    if (!cancelOrder) return

    cancelMutation.mutate(
      { id: cancelOrder.id },
      {
        onSuccess: () => {
          setCancelOrder(null)
        },
        onError: (error) => showError(error, '取消订单失败'),
      }
    )
  }, [cancelMutation, cancelOrder])

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }))
    setDraftParams((prev) => ({ ...prev, page }))
  }, [])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setParams((prev) => ({ ...prev, page: 1, page_size: pageSize }))
    setDraftParams((prev) => ({ ...prev, page: 1, page_size: pageSize }))
  }, [])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">手动订单</h1>
          <p className="text-muted-foreground">查看和管理您手动录入的订单</p>
        </div>
        {canDelivery && (
          <div>
            <input
              ref={importInputRef}
              data-testid="manual-order-delivery-import-input"
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleDeliveryImportChange}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={importDeliveryMutation.isPending}
              onClick={() => importInputRef.current?.click()}
            >
              {importDeliveryMutation.isPending ? '导入中...' : '导入发货 Excel'}
            </Button>
          </div>
        )}
      </div>

      <ManualOrderFilterBar
        params={draftParams}
        onParamsChange={handleDraftParamsChange}
        onSearch={handleSearch}
        onReset={handleReset}
        onStatusChange={handleStatusChange}
        onExport={canExport ? handleFilterExport : undefined}
        isExporting={isExporting}
      />

      <ManualOrderTable
        orders={data?.list || []}
        isLoading={isLoading}
        onViewDetail={handleViewDetail}
        onManualPayment={handleManualPayment}
        onConfirmPayment={handleConfirmPayment}
        onCancel={handleCancel}
        onApplyRefund={handleApplyRefundFromList}
        onDelivery={handleDelivery}
        onViewTracking={setTrackingOrder}
        onMarkReturned={(order) => setReturnDialog({ order })}
        onDelete={handleDelete}
        onBatchDelete={handleBatchDelete}
        onSelectedExport={canExport ? handleSelectedExport : undefined}
        onBatchPrint={handleBatchPrint}
        canConfirmPayment={canConfirmPayment}
        canCancel={canCancel}
        canApplyRefund={canApplyRefund}
        canDelivery={canDelivery}
        canMarkRefundReturned={canMarkRefundReturned}
        canDelete={canDelete}
      />

      <ListPagination
        page={params.page}
        pageSize={params.page_size}
        total={data?.total || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <ManualOrderDetailDrawer
        order={isDetailLoading ? null : detail?.order ?? null}
        open={detailOrderId !== null}
        onOpenChange={handleDetailOpenChange}
        scope={params.scope}
        orderStatusList={detail?.statuses}
        isStatusLoading={isDetailLoading}
        refundEligibility={detail?.refund_eligibility}
        onApplyRefund={canApplyRefund ? handleApplyRefund : undefined}
        onFlagsUpdated={() => {
          queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.lists() })
          queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.details() })
        }}
        onShippingUpdated={() => {
          queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.lists() })
          queryClient.invalidateQueries({ queryKey: employeeManualOrderKeys.details() })
        }}
      />

      <ManualOrderRefundDialog
        order={refundDialog?.order ?? null}
        eligibility={refundDialog?.eligibility}
        open={refundDialog !== null}
        isSubmitting={applyRefundMutation.isPending}
        onOpenChange={(open) => !open && setRefundDialog(null)}
        onSubmit={(data) => {
          if (!refundDialog) return
          applyRefundMutation.mutate(
            { id: refundDialog.order.id, data, scope: params.scope },
            {
              onSuccess: () => setRefundDialog(null),
              onError: (error) => showError(error, '申请退货失败'),
            }
          )
        }}
      />

      <ManualOrderReturnDialog
        order={returnDialog?.order ?? null}
        open={returnDialog !== null}
        isSubmitting={markReturnedMutation.isPending}
        onOpenChange={(open) => !open && setReturnDialog(null)}
        onSubmit={(data) => {
          if (!returnDialog) return
          markReturnedMutation.mutate(
            { id: returnDialog.order.id, data, scope: params.scope },
            {
              onSuccess: () => setReturnDialog(null),
              onError: (error) => showError(error, '登记退货失败'),
            }
          )
        }}
      />

      <ManualOrderDeliveryDialog
        order={deliveryOrder}
        open={deliveryOrder !== null}
        isSubmitting={deliveryMutation.isPending}
        onOpenChange={(open) => !open && setDeliveryOrder(null)}
        onSubmit={(data) => {
          if (!deliveryOrder) return
          deliveryMutation.mutate(
            { id: deliveryOrder.id, data },
            {
              onSuccess: () => setDeliveryOrder(null),
              onError: (error) => showError(error, '发货失败'),
            }
          )
        }}
      />

      <Sheet
        open={trackingOrder !== null}
        onOpenChange={(open) => {
          if (!open) setTrackingOrder(null)
        }}
      >
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>物流轨迹</SheetTitle>
            {trackingOrder && <SheetDescription>订单号：{trackingOrder.order_id}</SheetDescription>}
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-140px)]">
            {trackingOrder && (
              <ExpressTrackingTimeline
                orderId={trackingOrder.id}
                scope={params.scope}
                comName={trackingOrder.delivery_name}
                nu={trackingOrder.delivery_id}
              />
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      <ManualOrderPaymentDialog
        orderId={paymentOrderId}
        open={paymentOrderId !== null}
        onOpenChange={(open) => !open && setPaymentOrderId(null)}
      />

      <ManualOrderConfirmPaymentDialog
        order={confirmPaymentOrder}
        open={confirmPaymentOrder !== null}
        isSubmitting={confirmPaymentMutation.isPending}
        onOpenChange={(open) => !open && setConfirmPaymentOrder(null)}
        onConfirm={handleSubmitConfirmPayment}
      />

      <ManualOrderCancelDialog
        order={cancelOrder}
        open={cancelOrder !== null}
        isSubmitting={cancelMutation.isPending}
        onOpenChange={(open) => !open && setCancelOrder(null)}
        onConfirm={handleConfirmCancel}
      />

      <ManualOrderDeleteDialog
        order={deleteOrder}
        open={deleteOrder !== null}
        isSubmitting={deleteMutation.isPending}
        onOpenChange={(open) => !open && setDeleteOrder(null)}
        onConfirm={handleConfirmDelete}
      />

      <ManualOrderDeleteDialog
        order={null}
        count={batchDeleteIds?.length ?? 0}
        open={batchDeleteIds !== null && batchDeleteIds.length > 0}
        isSubmitting={batchDeleteMutation.isPending}
        onOpenChange={(open) => !open && setBatchDeleteIds(null)}
        onConfirm={handleConfirmBatchDelete}
      />
    </div>
  )
}
